import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

export default function LoginPage({ onSwitchMode }) {
  const { login } = useApp();
  const [password, setPassword] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const revealTimeoutRef = useRef(null);

  // Handle the peek-then-hide effect
  useEffect(() => {
    if (showPassword) {
      setDisplayValue(password);
      return;
    }

    // Clear any existing timeout
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
    }

    if (password.length === 0) {
      setDisplayValue('');
      return;
    }

    // Show last character briefly, rest as bullets
    const bullets = '\u2022'.repeat(password.length - 1);
    const lastChar = password[password.length - 1];
    setDisplayValue(bullets + lastChar);

    // After 800ms, convert all to bullets
    revealTimeoutRef.current = setTimeout(() => {
      setDisplayValue('\u2022'.repeat(password.length));
    }, 800);

    return () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
    };
  }, [password, showPassword]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Determine the actual password based on input
    if (inputValue.length > password.length) {
      // Character added - get the new character (could be anywhere due to cursor position)
      // For simplicity, assume typing at the end
      const newChar = inputValue.replace(/\u2022/g, '').slice(-1) || inputValue.slice(-1);
      setPassword(prev => prev + newChar);
    } else if (inputValue.length < password.length) {
      // Character removed
      const diff = password.length - inputValue.length;
      setPassword(prev => prev.slice(0, -diff));
    }
    
    setError('');
  };

  // Alternative: Direct password input handling
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      setPassword(prev => prev.slice(0, -1));
      e.preventDefault();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      setPassword(prev => prev + e.key);
      e.preventDefault();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (login(password)) {
      // Success - context will handle redirect
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
      setDisplayValue('');
    }
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header with logo */}
      <div className="bg-[#0055A4] py-8 px-4 relative">
        {onSwitchMode && (
          <button
            onClick={onSwitchMode}
            className="absolute top-4 left-4 text-white/80 hover:text-white flex items-center gap-1 text-sm"
            data-testid="holiday-back-to-programs"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        )}
        <motion.div 
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img 
            src={logoUrl} 
            alt="Rolling River Day Camp" 
            className="w-32 h-32 rounded-full bg-white p-1 shadow-lg"
          />
          <h1 className="mt-4 text-2xl font-bold text-white font-['Fredoka']">
            Rolling River Day Camp
          </h1>
          <p className="text-white/80 text-sm mt-1">Holiday Camp Attendance</p>
        </motion.div>
      </div>

      {/* Login form */}
      <motion.div 
        className="flex-1 flex items-center justify-center px-6 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E2E8F0]">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-[#0055A4]/10 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-[#0055A4]" />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-center text-[#0F172A] font-['Fredoka'] mb-2">
              Welcome Back!
            </h2>
            <p className="text-sm text-center text-[#475569] mb-6">
              Enter your password to access the attendance system
            </p>

            <form onSubmit={handleSubmit}>
              <div className="relative mb-4">
                <input
                  ref={inputRef}
                  type="text"
                  data-testid="login-password-input"
                  placeholder="Enter password"
                  value={showPassword ? password : displayValue}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => {
                    // Fallback for paste/autocomplete
                    if (showPassword) {
                      setPassword(e.target.value);
                    }
                  }}
                  className="w-full h-14 rounded-xl border border-[#E2E8F0] bg-white text-black placeholder:text-gray-400 pr-12 pl-4 text-lg font-medium focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 focus:outline-none transition-all"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {error && (
                <motion.p 
                  className="text-red-500 text-sm text-center mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="login-error-message"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full h-14 bg-[#0055A4] hover:bg-[#004080] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                Sign In
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-[#94A3B8] mt-6">
            Holiday Camp Spring 2026
          </p>
        </div>
      </motion.div>
    </div>
  );
}
